import { useCallback, useEffect, useState } from "react"
import api from "../services/api"
import { apiPath } from "../config/paths"
import AdminLayout from "../components/AdminLayout"
import FileIcon from "../components/FileIcon"
import { useAdminFeedback } from "../context/AdminFeedbackContext"

const C = {
    navy: "#002B54",
    navyMid: "#1e4070",
    blue: "#1a6fc4",
    bluePale: "#e8f0fb",
    bluePale2: "#dceaf8",
    white: "#ffffff",
    offWhite: "#f5f7fa",
    gray100: "#eef1f5",
    gray200: "#dde2ea",
    gray300: "#c3cad5",
    gray400: "#94a0b0",
    gray500: "#64748b",
    gray700: "#334155",
    green: "#15803d",
    greenBg: "#dcfce7",
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export default function NDAManagement() {
    const { toast } = useAdminFeedback()
    const [ndas, setNdas] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState("")
    const [viewingNdaId, setViewingNdaId] = useState(null)
    const [isViewerOpen, setIsViewerOpen] = useState(false)
    const [viewerBlobUrl, setViewerBlobUrl] = useState("")
    const [viewError, setViewError] = useState("")
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [total, setTotal] = useState(0)
    const [totalPages, setTotalPages] = useState(1)

    const fetchNDAs = useCallback(async (overrideSearch) => {
        try {
            setLoading(true)
            const search = overrideSearch !== undefined ? overrideSearch : searchTerm
            const params = new URLSearchParams({ page, limit })
            if (search.trim()) params.set("search", search.trim())
            const res = await api.get(`/admin/ndas?${params}`)
            setNdas(res.data.records)
            setTotal(res.data.total)
            setTotalPages(res.data.totalPages)
        } catch (err) {
            console.error("Error fetching NDAs:", err)
        } finally {
            setLoading(false)
        }
    }, [page, limit, searchTerm])

    useEffect(() => { fetchNDAs() }, [fetchNDAs])

    const handleSearch = (e) => {
        e.preventDefault()
        setPage(1)
        fetchNDAs()
    }

    const clearSearch = () => {
        setSearchTerm("")
        setPage(1)
        fetchNDAs("")
    }

    const handleExportCsv = async () => {
        try {
            const params = new URLSearchParams({ exportCsv: "true" })
            if (searchTerm.trim()) params.set("search", searchTerm.trim())
            const res = await api.get(`/admin/ndas?${params}`, { responseType: "blob" })
            const blob = new Blob([res.data], { type: "text/csv" })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `nda-export-${Date.now()}.csv`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (err) {
            console.error("Export failed:", err)
            toast.error("Failed to export NDAs. Please try again.")
        }
    }

    function getPageNumbers() {
        const pages = []
        const maxVisible = 5
        if (totalPages <= maxVisible + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i)
        } else {
            pages.push(1)
            let start = Math.max(2, page - 1)
            let end = Math.min(totalPages - 1, page + 1)
            if (page <= 3) { start = 2; end = Math.min(maxVisible, totalPages - 1) }
            else if (page >= totalPages - 2) { start = Math.max(totalPages - maxVisible + 1, 2); end = totalPages - 1 }
            if (start > 2) pages.push("...")
            for (let i = start; i <= end; i++) pages.push(i)
            if (end < totalPages - 1) pages.push("...")
            pages.push(totalPages)
        }
        return pages
    }

    const handleViewNDA = (id) => {
        setViewError("")
        setViewingNdaId(id)
        setViewerBlobUrl(`${apiPath(`/admin/ndas/view/${id}`)}#toolbar=0`)
        setIsViewerOpen(true)
        setViewingNdaId(null)
    }

    return (
        <AdminLayout
            title="Signed NDAs"
            description="Manage and search through signed Non-Disclosure Agreements."
        >
            <div style={{ width: "100%", maxWidth: "none", margin: 0 }}>

                {viewError && (
                    <div style={{
                        background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "10px",
                        padding: "12px 16px", marginBottom: "20px", fontSize: "13px",
                        color: "#b91c1c", fontWeight: "500",
                    }}>
                        {viewError}
                    </div>
                )}

                {/* Search Bar + Export */}
                <div style={{ marginBottom: "24px" }}>
                    <form onSubmit={handleSearch} style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                flex: 1, padding: "12px 18px", borderRadius: "12px",
                                border: `1.5px solid ${C.gray200}`, fontSize: "14px",
                                outline: "none", transition: "border 0.2s"
                            }}
                        />
                        <button
                            type="submit"
                            style={{
                                padding: "12px 24px",
                                borderRadius: "12px",
                                border: "none",
                                background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                                color: C.white,
                                fontSize: "13px",
                                fontWeight: "700",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                alignSelf: "stretch",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            Search
                        </button>
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={clearSearch}
                            >
                                Clear
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleExportCsv}
                            style={{
                                padding: "12px 20px",
                                borderRadius: "12px",
                                border: `1.5px solid ${C.green}`,
                                background: C.greenBg,
                                color: C.green,
                                fontSize: "13px",
                                fontWeight: "700",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                transition: "all 0.2s",
                            }}
                        >
                            Export CSV
                        </button>
                    </form>
                </div>

                {/* Table */}
                <div style={{
                    background: C.white, borderRadius: "16px", border: `1px solid ${C.gray200}`,
                    overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
                }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ background: "#f8fafc", borderBottom: `2px solid ${C.gray100}` }}>
                                {["Full Name", "Email Address", "Company", "Documents", "Signed Date", "Actions"].map(h => (
                                    <th key={h} style={{
                                        padding: "16px 20px", textAlign: "left", fontSize: "11px",
                                        fontWeight: "800", textTransform: "uppercase", color: C.gray400,
                                        letterSpacing: "0.5px"
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" style={{ padding: "40px", textAlign: "center", color: C.gray400 }}>
                                        Loading records...
                                    </td>
                                </tr>
                            ) : ndas.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ padding: "40px", textAlign: "center", color: C.gray400 }}>
                                        No signed NDAs found.
                                    </td>
                                </tr>
                            ) : (
                                ndas.map((nda, idx) => (
                                    <tr key={nda._id} style={{
                                        borderBottom: idx === ndas.length - 1 ? "none" : `1px solid ${C.gray100}`,
                                        transition: "background 0.1s"
                                    }}>
                                        <td style={{ padding: "16px 20px", fontSize: "14px", fontWeight: "700", color: C.navy }}>
                                            {nda.fullName || "—"}
                                        </td>
                                        <td style={{ padding: "16px 20px", fontSize: "14px", color: C.gray500 }}>
                                            {nda.email}
                                        </td>
                                        <td style={{ padding: "16px 20px", fontSize: "13px", color: C.gray700 }}>
                                            {nda.company || <span style={{ color: C.gray300, fontStyle: "italic" }}>—</span>}
                                        </td>
                                        <td style={{ padding: "16px 20px", maxWidth: "220px" }}>
                                            {nda.documents && nda.documents.length > 0 ? (
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                                    {nda.documents.slice(0, 2).map((doc, i) => (
                                                        <span key={i} style={{
                                                            display: "inline-flex", alignItems: "center", gap: "4px",
                                                            background: C.bluePale, border: `1px solid ${C.bluePale2}`,
                                                            borderRadius: "6px", padding: "3px 8px",
                                                            fontSize: "11px", fontWeight: "600", color: C.blue,
                                                            maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                                                        }} title={doc}>
                                                            <FileIcon size={10} color={C.blue} />
                                                            {doc}
                                                        </span>
                                                    ))}
                                                    {nda.documents.length > 2 && (
                                                        <span style={{
                                                            display: "inline-flex", alignItems: "center",
                                                            background: C.gray100, borderRadius: "6px", padding: "3px 8px",
                                                            fontSize: "11px", fontWeight: "700", color: C.gray500,
                                                        }} title={nda.documents.slice(2).join(", ")}>
                                                            +{nda.documents.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: C.gray300, fontStyle: "italic", fontSize: "12px" }}>No access granted</span>
                                            )}
                                        </td>
                                        <td style={{ padding: "16px 20px", fontSize: "13px", color: C.gray400 }}>
                                            {new Date(nda.signedAt || nda.requestedAt).toLocaleDateString("en-GB", {
                                                day: "2-digit", month: "short", year: "numeric"
                                            })}
                                        </td>
                                        <td style={{ padding: "16px 20px" }}>
                                            <button
                                                onClick={() => handleViewNDA(nda._id)}
                                                disabled={viewingNdaId === nda._id}
                                                className="btn btn-outline"
                                                style={{ fontSize: "12px", padding: "6px 12px" }}
                                            >
                                                {viewingNdaId === nda._id ? "Opening..." : <><span>View Document</span> <FileIcon size={14} color="#0183FF" style={{ verticalAlign: "middle", marginLeft: "4px" }} /></>}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Pagination Footer */}
                    {ndas.length > 0 && (
                        <div style={{
                            padding: "14px 24px", borderTop: `1px solid ${C.gray100}`, background: C.offWhite,
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ fontSize: "12px", color: C.gray500, fontWeight: "600" }}>Rows per page:</span>
                                    <select
                                        value={limit}
                                        onChange={(e) => { setLimit(Number(e.target.value)); setPage(1) }}
                                        style={{
                                            padding: "4px 8px", borderRadius: "6px",
                                            border: `1px solid ${C.gray200}`, fontSize: "12px", fontWeight: "700",
                                            color: C.navy, background: C.white, cursor: "pointer", outline: "none",
                                        }}
                                    >
                                        {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <span style={{ fontSize: "12px", color: C.gray400 }}>
                                    {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)} of {total}
                                </span>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <button onClick={() => setPage(1)} disabled={page <= 1} title="First page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy, cursor: page <= 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>«</button>
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} title="Previous page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy, cursor: page <= 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>

                                {getPageNumbers().map((p, i) =>
                                    p === "..." ? (
                                        <span key={`ellipsis-${i}`} style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", color: C.gray400, fontWeight: "700" }}>…</span>
                                    ) : (
                                        <button key={p} onClick={() => setPage(p)}
                                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: p === page ? "none" : `1px solid ${C.gray200}`, background: p === page ? `linear-gradient(135deg, ${C.navy}, ${C.navyMid})` : C.white, fontSize: "12px", fontWeight: "700", color: p === page ? C.white : C.navy, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{p}</button>
                                    )
                                )}

                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} title="Next page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy, cursor: page >= totalPages ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
                                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} title="Last page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy, cursor: page >= totalPages ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>»</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* SECURE DOCUMENT VIEWER MODAL */}
            {isViewerOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.85)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div style={{
                        background: 'white', borderRadius: '16px', width: '95vw', maxWidth: '1200px',
                        height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }}>
                        <div style={{
                            padding: '16px 24px', borderBottom: '1px solid #eee',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <span style={{ fontWeight: 700, color: C.navy }}>NDA Document Viewer</span>
                            <button
                                onClick={() => {
                                    setIsViewerOpen(false)
                                    setViewerBlobUrl("")
                                }}
                                style={{
                                    border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer',
                                    color: C.gray500, lineHeight: 1
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        <div style={{ flex: 1, background: '#f1f5f9' }}>
                            <iframe
                                src={viewerBlobUrl}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                title="NDA Viewer"
                            />
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}
